import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import type { RadioChangeEvent, SelectProps, UploadFile } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CAMPAIGN_TEMPLATES_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  ChannelKeyMap,
  RfmPeriod,
} from "../constants/labels";
import {
  CampaignTypeEnum,
  ChannelEnum,
  ModeEnum,
  RFMPeriod,
  StatusEnum,
  TemplatesTypeEnum,
  TypeEnum,
} from "../constants/enums";
import { brandMemberSegmentService } from "../services/brandMemberSegmentService";
import { useBrandStore } from "../store/brandStore";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import {
  CheckCircleFilled,
  CloseOutlined,
  DeleteFilled,
  DeleteOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useWatch } from "antd/es/form/Form";
import { VARS } from "../constants/variables";
import { RoutePaths } from "../routes/route";
import Editor from "../components/common/Editor";
import { UploadChangeParam } from "antd/es/upload";
import { Image as AntdImage } from "antd";
import DOMPurify from "dompurify";
import {
  htmlToWhatsappText,
  mapToEVouchers,
  whatsappTextToHtml,
} from "../utils/convert";
import VoucherTable from "../components/common/VoucherTable";
import {
  CampaignDetails,
  EstimatedTotalMembersRequest,
  CampaignInfo,
} from "../models/campaign";
import { campaignService } from "../services/campaignService";
import { PostalDistricts } from "../models/postal";
import { useQueryNavigator } from "../utils/navigate";
import { DateRange } from "../models/time";
import {
  applyPastTimeToday,
  BlockRange,
  buildDisabledTime,
  parseBlockedRanges,
} from "../utils/date";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { CampaignFormValues } from "../models/formValues";
import { helperService } from "../services/helperService";
import RFMGeneratingModal from "../components/common/GeneratingDataModal";
import CustomModal from "../components/common/Modal";
import EditorQuill from "../components/common/EditorQuill";
import { getChannelCost } from "../constants/cost";
import { getSmsCharCount, getSmsSegments, isGsmText } from "../constants/text";
import { MessageTemplateData } from "../models/messageTemplates";
import MessageTemplatesModal from "../components/common/MessageTemplatesModal";
import PreviewMemberModal from "../components/common/PreviewMemberModal";
import BillingModal from "../components/common/BillingModal";

const { Title, Text } = Typography;

const FieldRow: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <Row gutter={16} align="top" style={{ marginBottom: 12 }}>
    <Col xs={24} md={10} lg={8}>
      <Text>
        {label} {required && <Text type="danger">*</Text>}
      </Text>
    </Col>
    <Col xs={24} md={14} lg={16}>
      {children}
    </Col>
  </Row>
);

const UpdateCampaign: React.FC = () => {
  // ======================================================
  // 📱 Device Detection
  // ======================================================
  const isMobileOrTablet = window.innerWidth <= 1024;

  // ======================================================
  // 📝 Form & Navigation
  // ======================================================
  const [form] = Form.useForm<CampaignFormValues>();
  const location = useLocation();
  const { brandId, campaignInfo } = useBrandStore();
  const navigate = useNavigate();

  // ======================================================
  // 📊 UI State Management
  // ======================================================
  const [scheduleType, setScheduleType] = useState<number>(TypeEnum.Onetime);
  const [targetMemberMode, setTargetMemberMode] = useState<number>(
    ModeEnum.Refresh,
  );
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [firstReloadMember, setFirstReloadMember] = useState(true);
  const [changeRFM, setChangeRFM] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);

  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] =
    useState(false);
  const [isPaymentFailedModalOpen, setIsPaymentFailedModalOpen] =
    useState(false);
  const [pendingPayload, setPendingPayload] = useState<CampaignDetails | null>(
    null,
  );
  const [loadingVoucher, setLoadingVoucher] = useState(false);
  const [link, setLink] = useState("");
  const [enableTrackingLink, setEnableTrackingLink] = useState<boolean>(true);

  const [listMessageTemplates, setListMessageTemplates] = useState<
    MessageTemplateData[]
  >([]);
  const [linkOS, setLinkOS] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>("");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplatesTypeEnum>();
  const [inactiveDays, setInactiveDays] = useState(60);

  const [
    isModalWarningCreateCampaignVisible,
    setIsModalWarningCreateCampaignVisible,
  ] = useState(false);
  const [selectedCampaignChannel, setSelectedCampaignChannel] =
    useState<ChannelEnum | null>(null);
  const [messageTemplatesOpen, setMessageTemplatesOpen] = useState(false);
  const [selectedMessageTemplates, setSelectedMessageTemplates] =
    useState<MessageTemplateData | null>(null);
  const templatesSummary = selectedMessageTemplates
    ? `${selectedMessageTemplates.Name}`
    : "No templates selected";
  const [loading, setLoading] = useState(false);
  const [loadingRefreshMember, setLoadingRefreshMember] = useState(false);
  const [estimatedTotalMember, setEstimatedTotalMember] = useState(0);
  const [estimatedMember, setEstimatedMember] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);
  const [segmentLinkIdBySegmentId, setSegmentLinkIdBySegmentId] = useState<
    Map<number, number>
  >(new Map());
  const [postalLinkIdByDistrictId, setPostalLinkIdByDistrictId] = useState<
    Map<number, number>
  >(new Map());
  const [messageId, setMessageId] = useState<number | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedEVoucher, setSelectedEVoucher] = useState<EVoucher | null>(
    null,
  );
  const [postalFilter, setPostalFilter] = useState<string[]>([]);
  const [alreadyReload, setAlreadyReload] = useState(false);
  const [searchVoucher, setSearchVoucher] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
  const [selectedRow, setSelectedRow] = useState<Voucher | null>(null);
  const [campaignDetails, setCampaignDetails] =
    useState<CampaignDetails | null>(null);
  const startOfToday = dayjs().startOf("day");
  const endOfNextMonth = dayjs().add(1, "month").endOf("month");
  const [dataPostalDistrict, setDataPostalDistrict] = useState<
    PostalDistricts[]
  >([]);
  const [listBlockDate, setListBlockDate] = useState<DateRange[]>([]);
  const [listVoucher, setListVoucher] = useState<Voucher[]>([]);
  const [listEVoucher, setListEVoucher] = useState<EVoucher[]>([]);
  const filteredVouchers = listVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(searchVoucher.toLowerCase()),
  );
  const filteredVEouchers = listEVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(searchVoucher.toLowerCase()),
  );
  const blocked: BlockRange[] = parseBlockedRanges(
    listBlockDate,
    "YYYY-MM-DD HH:mm",
  );
  const { search } = useLocation();
  const navigateWithQuery = useQueryNavigator();
  const { Option } = Select;

  const query = new URLSearchParams(search);
  const id = query.get("id");
  const [data, setData] = useState<CampaignDetails | null>(null);
  const isBilled = data?.CampaignRunLog?.some((log) => log.IsBilled === true);

  const selectedType = Form.useWatch("Type", form) ?? 0.0;

  const [showTimeout, setShowTimeout] = useState(false);
  const voucherVars = ["{VoucherName}", "{VoucherExpiry}"];

  const filteredVars =
    selectedType === CampaignTypeEnum.Voucher ||
    selectedType === CampaignTypeEnum.VoucherPoint
      ? VARS
      : VARS.filter((v) => !voucherVars.includes(v));
  // ======================================================
  // 📂 Criteria & RFM Selections
  // ======================================================
  const criteriaProfileValue = useWatch("Profile", form);
  const periodValue = useWatch("Period", form);
  const segmentValue = useWatch("Segments", form);
  const hasCriteria =
    criteriaProfileValue !== null && criteriaProfileValue !== undefined;
  const hasPeriod = !!periodValue;
  const hasSegment = Array.isArray(segmentValue)
    ? segmentValue.length > 0
    : !!segmentValue;
  // ======================================================
  // 📑 RFM Options (Segments / Profiles / Postal Codes)
  // ======================================================
  const AllBrand = "All Brands";

  const [selectedPurchaseBrand, setSelectedPurchaseBrand] = useState<string[]>(
    [],
  );
  const [listPurchaseBrandFilter, setListPurchaseBrandFilter] = useState<any[]>(
    [],
  );
  const [listBrandMemberSegment, setListBrandMemberSegment] = useState<
    { Id: number; Name: string }[]
  >([]);
  const [listBrandRFMProfile, setListBrandRFMProfile] = useState<
    { Id: number; Name: string; IsActive: boolean }[]
  >([]);

  const SegmentOptions: SelectProps["options"] = useMemo(
    () => listBrandMemberSegment.map((s) => ({ label: s.Name, value: s.Id })),
    [listBrandMemberSegment],
  );

  const ProfileOptions: SelectProps["options"] = useMemo(
    () =>
      [...listBrandRFMProfile].map((s) => ({
        label: s.IsActive ? `${s.Name} (Currently Selected)` : s.Name,
        value: s.Id,
      })),
    [listBrandRFMProfile],
  );

  const selectedPostalSectors = useMemo<string[]>(() => {
    if (!Array.isArray(dataPostalDistrict) || !postalFilter?.length) return [];

    const idSet = new Set(postalFilter.map((v) => String(v)));

    const merged = dataPostalDistrict
      .filter((d) => idSet.has(String(d.Id)))
      .flatMap((d) => d.PostalSectors ?? []);

    return Array.from(new Set(merged));
  }, [postalFilter, dataPostalDistrict]);

  const PostalCodeOptions: SelectProps["options"] = useMemo(
    () =>
      dataPostalDistrict.map((s) => ({
        label: `${s.PostalSectors.join(", ")} – ${s.GeneralLocation}`,
        value: String(s.Id),
        search: `${s.PostalSectors.join(" ")} ${s.GeneralLocation}`,
      })),
    [dataPostalDistrict],
  );
  // ======================================================
  // 💬 Message Handling
  // ====================================================
  const GSM7_REGEX =
    /^[@£$¥èéùìòÇ\n\rØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?A-ZÄÖÑÜ§a-zäöñüà¡¿^{}\[~\]|€]*$/;

  const isUnicode = (text: any) => !GSM7_REGEX.test(text);
  const msg = Form.useWatch("Message", form) ?? "";
  // const charCount = htmlToWhatsappText(msg || "").length;
  const isUnicodeMessage = isUnicode(msg);
  const MAX_LENGTH = isUnicodeMessage ? 70 : 160;
  const isVoucher =
    selectedType === CampaignTypeEnum.Voucher ||
    selectedType === CampaignTypeEnum.VoucherPoint;

  const actionText = isVoucher ? "Redeem Voucher" : "Order Now";
  const additionalMessageText = `\n\n${actionText}:\n${link}\n\nTo Unsubscribe:\nhttps://getz.co/a/UNSUB`;
  const additionalMessageWhatsappText = `\n\n👉 Reply with *OK to receive your membership login link*\n👉 Reply with *STOP to opt out of future WhatsApp messages*`;
  let finalMessage = msg + (enableTrackingLink ? additionalMessageText : "");
  finalMessage = finalMessage.replace(/\r\n/g, "\n");
  const finalHtmlMessage = msg;
  const charCount = getSmsCharCount(finalMessage);

  const isUnicodeText = !isGsmText(finalMessage);
  const smsSegments = getSmsSegments(finalMessage, isUnicodeText);

  const previewHtml = finalMessage
    ? DOMPurify.sanitize(
        selectedCampaignChannel === ChannelEnum.Sms
          ? finalMessage.replace(/\n/g, "<br />")
          : finalHtmlMessage,
      )
    : "Your Message will appear here...";

  const insertVar = (v: string) => {
    const voucherName = selectedVoucher?.Name ?? "";
    const voucherExpiry =
      selectedVoucher?.EndDateFormated ??
      selectedVoucher?.IssuanceEndDate ??
      "";

    if (v === "{VoucherName}") {
      v = voucherName;
    }

    if (v === "{VoucherExpiry}") {
      v = voucherExpiry ? dayjs(voucherExpiry).format("DD MMM YYYY") : "";
    }

    const html = form.getFieldValue("Message") || "";
    const lower = html.toLowerCase();
    const closeIdx = lower.lastIndexOf("</p>");

    let next: string;

    if (closeIdx !== -1) {
      const before = html.slice(0, closeIdx);
      const after = html.slice(closeIdx);

      const needsSpace =
        before.length > 0 && !/[ \t\n]$/.test(before.slice(-1));
      const spacer = needsSpace ? " " : "";

      next = before + spacer + v + after;
    } else {
      const needsSpace = html.length > 0 && !/[ \t\n]$/.test(html);
      next = html + (needsSpace ? " " : "") + v;
    }

    form.setFieldsValue({ Message: next });
  };

  // ======================================================
  // 📆 Campaign Details
  // ======================================================

  const campaignName = Form.useWatch("Name", form) ?? 0.0;
  const campaignDate = Form.useWatch("CampaignDate", form);
  const cost = Form.useWatch("CostPerMember", form) ?? 0.0;
  const budget = Form.useWatch("Budget", form) ?? 0;

  const maxReach = useMemo(() => {
    const budgetFinal = budget ?? 0;

    if (selectedCampaignChannel === ChannelEnum.Sms) {
      const realCostPerMember = cost * smsSegments;
      return realCostPerMember > 0
        ? Math.floor(budgetFinal / realCostPerMember)
        : 0;
    }

    return cost > 0 ? Math.floor(budgetFinal / cost) : 0;
  }, [budget, cost, smsSegments, selectedCampaignChannel]);

  const calculateCampaignEndDate = (
    startDate: string | dayjs.Dayjs,
    memberCount: number,
    channel: ChannelEnum | null,
  ) => {
    const start = dayjs(startDate);
    let end = start;

    switch (channel) {
      case ChannelEnum.Sms: {
        const totalSeconds = Math.max(memberCount, 60);
        end = start.add(totalSeconds, "second");
        break;
      }

      case ChannelEnum.WhatsAppStandard: {
        //LIMIT AVOID BAN
        const dailyLimit = 30;
        const daysNeeded = Math.max(1, Math.ceil(memberCount / dailyLimit));
        end = start.add(daysNeeded, "day");
        break;
      }

      case ChannelEnum.WhatsAppPremium: {
        const dailyLimit = 250;
        const daysNeeded = Math.max(1, Math.ceil(memberCount / dailyLimit));
        end = start.add(daysNeeded, "day");
        break;
      }

      default: {
        end = start.add(12, "day");
        break;
      }
    }

    return end.format("YYYY-MM-DD HH:mm");
  };

  const { formattedStart, formattedEnd } = useMemo(() => {
    if (!campaignDate) {
      return { formattedStart: "", formattedEnd: "" };
    }

    return {
      formattedStart: dayjs(campaignDate).format("YYYY-MM-DD HH:mm"),
      formattedEnd: calculateCampaignEndDate(
        campaignDate,
        estimatedTotalMember,
        selectedCampaignChannel,
      ),
    };
  }, [campaignDate, estimatedTotalMember, selectedCampaignChannel]);
  // ======================================================
  // 🔄 Member Refresh Logic
  // ======================================================

  const onChangeRFM = () => {
    setChangeRFM(true);
    setAlreadyReload(false);
  };

  const onChangeTemplate = (value: any) => {
    if (value === TemplatesTypeEnum.Default) {
      form.setFieldsValue({ InactiveDays: 60 });
      setInactiveDays(60);
    }
    setSelectedTemplate(value);

    onChangeRFM();
  };

  const postalTotal = useMemo(
    () => PostalCodeOptions.filter((o) => o.value !== "all").length,
    [PostalCodeOptions],
  );

  const handleRefreshMember = async () => {
    setLoadingRefreshMember(true);

    try {
      if (firstReloadMember) {
        setFirstReloadMember(false);
      }
      const data: EstimatedTotalMembersRequest = {
        BrandId: brandId ?? "",
        RFMProfile: criteriaProfileValue ?? 0,
        RFMPeriod: periodValue ?? 0,
        SegmentIds: segmentValue ?? [],
        IncludeListMember: false,
        FromAge: form.getFieldValue("ageRange")?.from,
        ToAge: form.getFieldValue("ageRange")?.to,
        PostCode: selectedPostalSectors,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : selectedPurchaseBrand,
        CampaignType: selectedType,
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
      };
      const result = await campaignService.estimatedTotalMember(data);
      setEstimatedTotalMember(result.EstimatedCountMember);
      setEstimatedMember(result.ListMember);
      if (result.EstimatedCountMember > 0) {
        form.setFields([
          {
            name: "TotalMembers" as any,
            errors: [],
          },
        ]);
      }
      setChangeRFM(false);
      setAlreadyReload(true);
      return result;
    } catch (error) {
      console.error("Failed to refresh member:", error);
    } finally {
      setLoadingRefreshMember(false);
    }
  };

  // ======================================================
  // 📅 Schedule Type Switching
  // ======================================================
  const onScheduleChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setScheduleType(val);
  };

  const onModeChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setTargetMemberMode(val);
  };

  // ======================================================
  // 🚀 Form Submission
  // ======================================================
  const handleSubmit = async (values: CampaignFormValues) => {
    try {
      const estimated = await handleRefreshMember();
      if (estimated.EstimatedCountMember === 0) {
        form.setFields([
          {
            name: "TotalMembers" as any,
            errors: ["No members matched the selected criteria."],
          },
        ]);
        form.scrollToField("Name" as any, {
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      const nextEstimatedCost =
        selectedCampaignChannel === ChannelEnum.Sms
          ? estimated.EstimatedCountMember * cost * smsSegments
          : estimated.EstimatedCountMember * cost;

      const formattedCampaignEndDate = calculateCampaignEndDate(
        formattedStart,
        estimated.EstimatedCountMember,
        selectedCampaignChannel,
      );

      const payload: CampaignDetails = {
        Id: Number(id) ?? 0,
        Name: values.Name,
        Type: values.Type,
        RFMPeriod: values.Period,
        FromAge: values.ageRange?.from,
        ToAge: values.ageRange?.to,
        Messages: [
          {
            Content:
              selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                ? htmlToWhatsappText(values?.Message ?? "") +
                  additionalMessageWhatsappText
                : selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                  ? (selectedMessageTemplates?.Content ?? "")
                  : (values?.Message ?? "") + additionalMessageText,
            Link: link,
            CampaignTemplates:
              selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                ? selectedMessageTemplates
                : null,
          },
        ],

        Segments: (values.Segments ?? []).map((segId) => ({
          SegmentId: Number(segId),
          Id: segmentLinkIdBySegmentId.get(Number(segId)) ?? undefined,
        })),
        PostalDistricts: (values.PostalCode ?? []).map((districtId) => ({
          DistrictId: Number(districtId),
          Id: postalLinkIdByDistrictId.get(Number(districtId)) ?? undefined,
        })),
        RFMProfile: values.Profile,
        BrandId: brandId ?? undefined,
        Status: values.Status,
        VoucherId:
          selectedType === CampaignTypeEnum.VoucherPoint ||
          selectedType === CampaignTypeEnum.Voucher
            ? selectedVoucher?.Id
            : undefined,
        SKU: selectedEVoucher?.SKU ?? undefined,
        CampaignStart: formattedStart,
        CampaignEnd: formattedCampaignEndDate,
        Budget: values.Budget,
        EstimatedTotalMembers: estimated.EstimatedCountMember,
        ScheduleType: scheduleType,
        ImageUrl: previewUrl ?? undefined,
        Channel: selectedCampaignChannel ?? undefined,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : JSON.stringify(selectedPurchaseBrand),
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
        Mode: values.Mode,
        IsBilled: 0,
      };
      setCampaignDetails(payload);

      if (payload.Status === StatusEnum.Draft) {
        // Draft — skip billing, update directly
        setLoading(true);
        try {
          const result = await campaignService.updateCampaignDetails(payload);
          if (!result || result.status != 1) {
            setShowTimeout(true);
            return;
          }
          navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
        } catch {
          setShowTimeout(true);
        } finally {
          setLoading(false);
        }
      } else {
        setPendingPayload(payload);
        setIsBillingModalOpen(true);
      }
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    }
  };

  // Helper
  const isChannelEnum = (v: unknown): v is ChannelEnum =>
    typeof v === "number" && v in ChannelEnum;

  // ======================================================
  // 🗂 Data Fetching
  // ======================================================
  const fetchListAccountBrand = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getListAccountBrand(brandId);
        if (data) {
          setListPurchaseBrandFilter(
            Array.from(
              new Set(
                data
                  .map((s: any) => s.BrandName?.trim())
                  .filter((name: any) => name && name !== ""),
              ),
            ).sort((a: any, b: any) => a.localeCompare(b)),
          );
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };
  const fetchVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getAllVouchers();
        if (data) {
          setListVoucher(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchEVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const dataEVoucher = await campaignService.getAllEVouchers(
          campaignInfo?.Email ?? "",
        );
        if (dataEVoucher) {
          const mapped = mapToEVouchers(dataEVoucher);
          setListEVoucher(mapped);

          if (data?.SKU) {
            const selectedEVoucher = mapped.find((v) => v.SKU === data.SKU);
            if (selectedEVoucher) {
              setSelectedEVoucher(selectedEVoucher);
              setSelectedKey(selectedEVoucher.Id);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching E-Voucher:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };


  const fetchBlockDate = async () => {
    if (brandId && selectedCampaignChannel) {
      try {
        const data = await campaignService.getBlockedCampaignDateRanges(
          brandId,
          selectedCampaignChannel,
        );
        if (data) {
          setListBlockDate(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };
  const fetchPostalDistrict = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllPostalDistrict();
        if (data) {
          setDataPostalDistrict(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };
  const fetchBrandMemberSegment = async () => {
    if (brandId) {
      try {
        const data =
          await brandMemberSegmentService.listBrandMemberSegment(brandId);
        if (data) {
          setListBrandMemberSegment(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        if (data) {
          setListBrandRFMProfile(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchLinkOS = async () => {
    if (brandId) {
      try {
        const data = await helperService.getPrimaryDomainByBrandId(brandId);
        if (data) {
          setLinkOS(data);
        }
      } catch (error) {
        console.error("Error get link OS:", error);
      }
    }
  };

  const FetchCheckAvailableCampaignName = async (value: string) => {
    if (brandId) {
      try {
        const isAvailable = await campaignService.checkAvailableCampaignName(
          value,
          data?.Id ?? undefined,
        );
        return isAvailable;
      } catch (error) {
        console.error("Error Check Available Campaign Name:", error);
      }
    }
  };

  const fetchShortenLink = async (value: string) => {
    if (brandId) {
      try {
        const data = await helperService.getShortenLink(value);
        if (data) {
          setLink(data);
        }
        return data;
      } catch (error) {
        console.error("Error get shorten link:", error);
      }
    }
  };

  const fetchMessageTemplates = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllMessageTemplates();
        if (data) {
          setListMessageTemplates(data);
        }
      } catch (error) {
        console.error("Error fetch message templates:", error);
      }
    }
  };

  const estimatedTotalCost = useMemo(() => {
    if (selectedCampaignChannel === ChannelEnum.Sms) {
      return estimatedTotalMember * cost * smsSegments;
    }
    return estimatedTotalMember * cost;
  }, [estimatedTotalMember, cost, selectedCampaignChannel, smsSegments]);

  // ======================================================
  // ⚛️ Effects
  // ======================================================

  useEffect(() => {
    if (campaignInfo?.Email) {
      fetchEVoucherList();
    }
  }, [campaignInfo]);

  useEffect(() => {
    if (nameError === null) {
      form.validateFields(["Name"]);
    }
  }, [nameError]);

  useEffect(() => {
    if (SHOW_REWARD_TYPES.includes(selectedType))
      form.validateFields(["Voucher"]);
  }, [selectedVoucher, selectedEVoucher]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (brandId) {
        await fetchListAccountBrand();
        await fetchBrandMemberSegment();
        await fetchBrandRFMProfile();
        await fetchPostalDistrict();
        await fetchBlockDate();
        await fetchVoucherList();
        await fetchLinkOS();
        // await fetchMessageTemplates();
      }
    }, 100);
    const fetchSettings = async () => {};
    fetchSettings();

    return () => clearTimeout(timeout);
  }, [brandId, selectedCampaignChannel]);

  useEffect(() => {
    if (!data) return;

    if (data?.CampaignStart && data?.Status != StatusEnum.Draft) {
      const startDate = new Date(data.CampaignStart);
      const oneHourBefore = new Date(startDate.getTime() - 60 * 60 * 1000);
      const now = new Date();
      if (now > oneHourBefore) {
        setShowTimeout(true);
      }
    }
    if (data?.CampaignRunLog?.some((log) => log.IsBilled)) {
      setShowTimeout(true);
    }
  }, [data]);

  useEffect(() => {
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) {
      form.setFieldsValue({
        Message:
          whatsappTextToHtml(selectedMessageTemplates?.Content ?? "") ?? "",
      });
      form.validateFields(["Message"]);
    }
  }, [selectedCampaignChannel, selectedMessageTemplates, form]);

  // ======================================================
  // 📤 Upload Handling
  // ======================================================
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadingFile, setUploadingFile] = useState<UploadFile | null>(null);
  const [progressUpload, setProgressUpload] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [notif, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = () => {
    notif.error({
      message: "Error!",
      description:
        "An error occurred while uploading the image. Please try again with a different image or contact the administrator.",
      placement: "top",
    });
  };
  const uploadToS3 = async (file: File) => {
    let simInterval: number | undefined;
    let finished = false;
    const start = Date.now();

    const startSimulatedProgress = () => {
      setProgressUpload(1);
      simInterval = window.setInterval(() => {
        setProgressUpload((prev) => {
          if (finished) return prev;
          if (prev >= 75) return 75;
          const next = prev + 2;
          return next >= 75 ? 75 : next;
        });
        return;
      }, 100);
    };

    const clearSim = () => {
      if (simInterval) {
        window.clearInterval(simInterval);
        simInterval = undefined;
      }
    };

    try {
      setIsUploading(true);
      setProgressUpload(0);
      startSimulatedProgress();

      const res = await campaignService.uploadCampaignImage(file);
      finished = true;
      clearSim();
      if (!res || !res.result || !res.result.Url) {
        openNotificationWithIcon();
      }
      const elapsed = Date.now() - start;
      const remain = elapsed < 1000 ? 1000 - elapsed : 0;
      if (remain > 0) {
        await new Promise((r) => setTimeout(r, remain));
      }

      setProgressUpload(100);

      const imageUrl = res?.result?.Url as string | undefined;
      if (!imageUrl) throw new Error("Upload succeeded but URL is missing.");

      form?.setFieldsValue?.({ Image: imageUrl });
      setPreviewUrl(imageUrl);

      setFileList((lst) =>
        lst.length
          ? [
              {
                ...lst[0],
                status: "done",
                url: imageUrl,
                response: { Url: imageUrl },
              },
            ]
          : lst,
      );

      message.success("Image uploaded.");
    } catch (err: any) {
      finished = true;
      clearSim();
      message.error(
        err?.response?.data?.message || err?.message || "Upload failed",
      );
      setFileList([]);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      form?.setFieldsValue?.({ Image: undefined });
    } finally {
      setTimeout(() => setProgressUpload(0), 300);
      setIsUploading(false);
    }
  };

  const handleChangeUpload = (info: UploadChangeParam<UploadFile<any>>) => {
    const latestList = info.fileList.slice(-1);
    setFileList(latestList);
    setUploadingFile(latestList[0] ?? null);
    if (latestList.length) setProgressUpload(0);
  };

  const beforeUpload = (file: File) => {
    const isValidType = ["image/jpeg", "image/png"].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 <= 5;

    if (!isValidType) {
      message.error("Only JPG/PNG are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    const url = URL.createObjectURL(file);

    const item: UploadFile = {
      uid: `${Date.now()}`,
      name: file.name,
      status: "uploading",
      originFileObj: file as any,
      url,
      thumbUrl: url,
    };
    setFileList([item]);

    uploadToS3(file);

    return false;
  };
  function removeLink(html: string): string {
    return html
      .replace(/\r\n/g, "\n")
      .replace(
        /\n*\s*Redeem Voucher:[\s\S]*?To Unsubscribe:[\s\S]*?https:\/\/getz\.co\/a\/UNSUB/g,
        "",
      )
      .replace(
        /\n*\s*Order Now:[\s\S]*?To Unsubscribe:[\s\S]*?https:\/\/getz\.co\/a\/UNSUB/g,
        "",
      )
      .trim();
  }

  function removeWhatsappAdditionalMessage(text: string): string {
    return text.replace(additionalMessageWhatsappText, "").trim();
  }

  // ======================================================
  // 🔎 Search & Voucher Selection
  // ======================================================
  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileList([]);
    setUploadingFile(null);
    setProgressUpload(0);
  };

  const summary = selectedVoucher
    ? `${selectedVoucher.Name} | ${selectedVoucher.DiscountLabel}`
    : selectedEVoucher
      ? `${selectedEVoucher.Name} | ${selectedEVoucher.Price}`
      : "No rewards selected";

  const fetchCampaignData = async () => {
    if (id) {
      try {
        setLoading(true);
        const result = await campaignService.getCampaignDetailsById(Number(id));
        if (result) {
          const data = result?.Campaign;
          if (
            data.Status === StatusEnum.Completed ||
            data.Status === StatusEnum.Cancelled
          ) {
            navigateWithQuery(
              RoutePaths.IFRAME_TARGET_MARKETING_HOME,
              "id",
              null,
            );
          }
          if (result?.Voucher) {
            setSelectedVoucher(result?.Voucher);
          }
          setData(data);
          const values = mapCampaignToFormValues(data);
          form.setFieldsValue({
            ...values,
            PurchaseBrand: values.PurchaseBrand ?? undefined,
          });
          setSelectedPurchaseBrand(values?.PurchaseBrand ?? [AllBrand]);

          setScheduleType(values?.ScheduleType ?? TypeEnum.Onetime);

          if (typeof data.EstimatedTotalMembers === "number") {
            setEstimatedTotalMember(data.EstimatedTotalMembers);
            setAlreadyReload(true);
          }

          const postalIds = Array.isArray(data.PostalDistricts)
            ? data.PostalDistricts.map((p: any) => String(p.DistrictId))
            : [];
          setPostalFilter(postalIds);
          form.setFieldsValue({ PostalCode: postalIds });
          if (data.ImageUrl) {
            setPreviewUrl(data.ImageUrl);
            setFileList([
              {
                uid: String(data.Id ?? Date.now()),
                name: "campaign-image",
                status: "done",
                url: data.ImageUrl,
                thumbUrl: data.ImageUrl,
              },
            ]);
          } else {
            setPreviewUrl(null);
            setFileList([]);
          }

          const segMap = new Map<number, number>();
          (data.Segments ?? []).forEach((s: any) => {
            if (s?.SegmentId != null && s?.Id != null)
              segMap.set(Number(s.SegmentId), Number(s.Id));
          });
          setSegmentLinkIdBySegmentId(segMap);

          const pdMap = new Map<number, number>();
          (data.PostalDistricts ?? []).forEach((p: any) => {
            if (p?.DistrictId != null && p?.Id != null)
              pdMap.set(Number(p.DistrictId), Number(p.Id));
          });
          setPostalLinkIdByDistrictId(pdMap);

          const msgId =
            data.Messages && data.Messages.length > 0
              ? Number(data.Messages[0].Id)
              : null;
          setMessageId(isNaN(msgId as number) ? null : msgId);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const nn = <T,>(v: T | null | undefined): T | undefined => v ?? undefined;
  const isEmpty = (v: any) => v === undefined || v === null || v === "";
  const normalizeText = (input: string) =>
    input
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/\n{2,}/g, "\n")
      .replace(/[ \t]+/g, " ")
      .trim();
  const mapCampaignToFormValues = (
    data: CampaignDetails,
  ): Partial<CampaignFormValues> => {
    const segments = Array.isArray(data.Segments)
      ? data.Segments.map((s) => s.SegmentId)
      : [];

    const postalCodes = Array.isArray(data.PostalDistricts)
      ? data.PostalDistricts.map((p) => String(p.DistrictId))
      : [];
    if (data?.Channel !== null) {
      setSelectedCampaignChannel(data?.Channel as ChannelEnum);
    }
    const link = data.Messages?.[0]?.Link;
    if (link) {
      setLink(link);
    }
    if (data.Messages?.[0]?.CampaignTemplates) {
      setSelectedMessageTemplates(data.Messages?.[0]?.CampaignTemplates);
    }

    if (data.TemplateType) {
      setSelectedTemplate(data.TemplateType);
      if (data.InactiveDays) {
        setInactiveDays(data.InactiveDays);
      }
      form.setFieldsValue({
        TemplateType: data.TemplateType,
        InactiveDays: data.InactiveDays,
      });
    }
    const message = data.Messages?.[0]?.Content || "";

    const normalizedMessage = normalizeText(message);

    const hasUnsubscribe =
      normalizedMessage.includes("To Unsubscribe:") &&
      normalizedMessage.includes("https://getz.co/a/UNSUB");
    setEnableTrackingLink(hasUnsubscribe);

    let cleaned = removeLink(message);
    cleaned = removeWhatsappAdditionalMessage(cleaned);
    const formattedMessage =
      data.Channel === ChannelEnum.WhatsAppStandard ||
      data.Channel === ChannelEnum.WhatsAppPremium
        ? whatsappTextToHtml(cleaned)
        : cleaned;

    return {
      Name: nn(data.Name)!,
      Type: nn(data.Type),
      Status: nn(data.Status),
      Link: link,
      Period: nn(data.RFMPeriod),
      Segments: segments,
      Profile: nn(data.RFMProfile),

      ageRange:
        data.FromAge != null || data.ToAge != null
          ? { from: nn(data.FromAge) as number, to: nn(data.ToAge) as number }
          : undefined,

      PostalCode: postalCodes,
      CampaignDate: data.CampaignStart ? dayjs(data.CampaignStart) : undefined,
      Budget: nn(data.Budget),
      Message: nn(formattedMessage),
      Voucher: nn(data.VoucherId),
      Image: nn(data.ImageUrl),
      CostPerMember: getChannelCost(data?.Channel ?? null),
      PurchaseBrand:
        data?.PurchaseBrandList && data.PurchaseBrandList.length > 0
          ? data.PurchaseBrandList
          : [AllBrand],
      ScheduleType: data?.ScheduleType,
      Mode: data?.Mode,
    };
  };

  // const getChannelCost = (channel: ChannelEnum | null): number => {
  //   switch (channel) {
  //     case ChannelEnum.WhatsAppStandard:
  //       return Number(process.env.REACT_APP_WHATSAPP_COST) || 0;

  //     case ChannelEnum.WhatsAppPremium:
  //       return Number(process.env.REACT_APP_WHATSAPP_PREMIUM_COST) || 0;

  //     case ChannelEnum.Sms:
  //       return Number(process.env.REACT_APP_SMS_COST) || 0;

  //     default:
  //       return 0;
  //   }
  // };

  useEffect(() => {
    fetchCampaignData();
  }, [id]);

  const SHOW_REWARD_TYPES = [
    CampaignTypeEnum.Voucher,
    CampaignTypeEnum.VoucherPoint,
    CampaignTypeEnum.EVoucher,
  ];

  const handleChangePurchaseBrand = (values: string[]) => {
    if (
      values.includes(AllBrand) &&
      !selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }

    if (
      !values.includes(AllBrand) &&
      selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([]);
      return;
    }

    const allSelected = listPurchaseBrandFilter.every((b) =>
      values.includes(b),
    );
    if (allSelected) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }
    setSelectedPurchaseBrand(values.filter((v) => v !== AllBrand));
  };

  useEffect(() => {
    if (!listPurchaseBrandFilter?.length) return;

    if (
      selectedPurchaseBrand.includes(AllBrand) &&
      selectedPurchaseBrand.length === 1
    ) {
      const updated = [AllBrand, ...listPurchaseBrandFilter];
      setSelectedPurchaseBrand(updated);
      form.setFieldsValue({ PurchaseBrand: updated });
    } else {
      form.setFieldsValue({ PurchaseBrand: selectedPurchaseBrand });
    }
  }, [selectedPurchaseBrand, listPurchaseBrandFilter]);

  useEffect(() => {
    if (selectedTemplate === TemplatesTypeEnum.Default) {
      const allSegmentValues: number[] = (SegmentOptions ?? [])
        .map((option) => option.value)
        .filter((value): value is number => typeof value === "number");
      form.setFieldsValue({
        Period: RFMPeriod.Lifetime,
        Segments: allSegmentValues,
        Profile: listBrandRFMProfile?.find((s) => s.IsActive)?.Id,
      });
    }
  }, [selectedTemplate, ProfileOptions]);

  const handleError = ({ errorFields }: any) => {
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name, {
        behavior: "smooth",
        block: "center",
      });

      message.error(errorFields[0].errors?.[0]);
    }
  };

  const onPaymentLater = async () => {
    setIsBillingModalOpen(false);
    setLoading(true);
    try {
      const result = await campaignService.updateCampaignDetails(
        pendingPayload!,
      );
      if (!result || result.status != 1) {
        setShowTimeout(true);
        return;
      }
      navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
    } catch {
      setShowTimeout(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={handleError}
      initialValues={{
        ScheduleType: TypeEnum.Onetime,
      }}
    >
      {contextHolder}

      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                Update{" "}
                {selectedCampaignChannel
                  ? ChannelKeyMap[selectedCampaignChannel]
                  : ""}{" "}
                Campaign
              </Title>
            </Col>
            <Col>
              <Button
                color="primary"
                variant="outlined"
                onClick={() =>
                  navigateWithQuery(
                    RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                    "id",
                    null,
                  )
                }
              >
                Back
              </Button>
            </Col>
          </Row>
        }
        className="card-form-campaign"
      >
        {/* {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
          <Alert
            className=""
            type="info"
            description={
              <span>
                Campaign delivery is processed in batches, with a limit of 1,000
                recipients per day.
              </span>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )} */}

        {/* Campaign Details */}
        <Title level={5}>Campaign Details</Title>
        <Divider style={{ margin: "10px 0" }} />
        <Form.Item name="Status" hidden>
          <Input />
        </Form.Item>
        <FieldRow label="Campaign Name" required>
          <Form.Item
            name="Name"
            validateStatus={nameError ? "error" : ""}
            help={nameError}
            rules={[
              { required: true, message: "Please input campaign name" },
              {
                validator: (_, value) => {
                  if (!value) {
                    setNameError("Please input campaign name");
                  }
                  if (value && value.trim() === "") {
                    setNameError(
                      "Campaign name cannot be empty or spaces only",
                    );
                  }

                  const invalidCharRegex = /[^a-zA-Z0-9 _-]/;
                  if (invalidCharRegex.test(value)) {
                    setNameError(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                    return Promise.reject(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                  }
                  if (nameError) return Promise.reject(nameError);

                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder="Input Campaign Name"
              onChange={async (e) => {
                const value = e.target.value.trim();

                if (value) {
                  const generatedLink = `${linkOS}?utm_source=${
                    selectedCampaignChannel
                      ? ChannelKeyMap[selectedCampaignChannel]
                      : ""
                  }&utm_campaign=${value}&utm_medium=OS`;
                  const isAvailable =
                    await FetchCheckAvailableCampaignName(value);
                  if (isAvailable) {
                    setNameError(null);
                    form.setFields([{ name: "Name", errors: [] }]);

                    fetchShortenLink(generatedLink);
                  } else {
                    setNameError(
                      "A campaign with this name already exists. Please enter a unique campaign name",
                    );
                  }
                }
              }}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Campaign Type" required>
          <Form.Item
            name="Type"
            rules={[{ required: true, message: "Select Campaign Type" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="Select Campaign Type"
              options={CAMPAIGN_TYPE_OPTIONS}
              allowClear
              onChange={() => {
                setSelectedEVoucher(null);
                setSelectedVoucher(null);
                setSelectedMessageTemplates(null);
              }}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Campaign Template Type" required>
          <Form.Item
            name="TemplateType"
            rules={[
              { required: true, message: "Select Campaign Template Type" },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="Select Campaign Template Type"
              options={CAMPAIGN_TEMPLATES_OPTIONS}
              value={selectedTemplate}
              onChange={(value) => {
                onChangeTemplate(value);
              }}
            />
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              {selectedTemplate === TemplatesTypeEnum.Default
                ? `This template uses a pre-configured RFM segmentation targeting members with no transactions or visits in the last ${inactiveDays} days`
                : "Fully customizable RFM segmentation. Select your own period, segments and criteria."}
            </div>
          </Form.Item>
        </FieldRow>
        {selectedTemplate === TemplatesTypeEnum.Default && (
          <FieldRow label="Inactive Period (Days)">
            <Form.Item
              name="InactiveDays"
              rules={[{ required: true, message: "Please input days" }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                value={inactiveDays}
                onChange={(value) => setInactiveDays(value || 60)}
                placeholder="Inactive Days"
                addonAfter="Days"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Define the number of days since a members last transaction or
              visit to be considered inactive.
            </div>
          </FieldRow>
        )}
        <FieldRow label="Selected RFM Period" required>
          <Form.Item
            name="Period"
            rules={[{ required: true, message: "Please select RFM Period" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              onChange={onChangeRFM}
              placeholder="Select RFM Period"
              options={RfmPeriod}
              style={{ width: "100%" }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>

        <FieldRow label="Target RFM Segment" required>
          <Form.Item
            name="Segments"
            rules={[{ required: true, message: "Select Target RFM Segment" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              onChange={onChangeRFM}
              mode="multiple"
              placeholder="Select Target RFM Segment"
              options={SegmentOptions}
              allowClear
              style={{ width: "100%" }}
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) => {
                const total = SegmentOptions?.length ?? 0;
                const selected = omittedValues?.length ?? 0;

                if (selected === 0) return null;
                if (selected === total) return "All Segments";

                return `${selected}/${total} selected`;
              }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="RFM Criteria Profile" required>
          <Form.Item
            style={{ marginBottom: 0 }}
            name="Profile"
            rules={[
              { required: true, message: "Please select RFM Criteria Profile" },
            ]}
          >
            <Select
              onChange={onChangeRFM}
              placeholder="Select RFM Criteria Profile"
              options={ProfileOptions}
              style={{ width: "100%" }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Target Demographics (age)">
          <Row gutter={[12, 12]}>
            <Col xl={4} xs={10}>
              <Form.Item
                name={["ageRange", "from"]}
                dependencies={[["ageRange", "to"]]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const to = getFieldValue(["ageRange", "to"]);
                      if (isEmpty(value) && isEmpty(to))
                        return Promise.resolve();
                      if (isEmpty(value) && !isEmpty(to)) {
                        return Promise.reject(
                          new Error("Please input minimum age"),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  min={0}
                  onChange={onChangeRFM}
                  placeholder="From Age"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={1} style={{ textAlign: "center", lineHeight: "32px" }}>
              <Divider
                style={{
                  margin: "15px 0",
                  borderBlockStart: "1px solid black",
                }}
              />
            </Col>
            <Col xl={4} xs={10}>
              <Form.Item
                name={["ageRange", "to"]}
                dependencies={[["ageRange", "from"]]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const from = getFieldValue(["ageRange", "from"]);
                      if (isEmpty(value) && isEmpty(from))
                        return Promise.resolve();
                      if (!isEmpty(from) && isEmpty(value)) {
                        return Promise.reject(
                          new Error("Please input maximum age"),
                        );
                      }
                      if (!isEmpty(from) && !isEmpty(value)) {
                        const a = Number(from);
                        const b = Number(value);
                        if (!(b > a)) {
                          return Promise.reject(
                            new Error("To age must be higher than From age"),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  min={0}
                  placeholder="To Age"
                  style={{ width: "100%" }}
                  onChange={onChangeRFM}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Specify the age range of members to include in the campaign.
            </div>
          </Row>
        </FieldRow>
        <FieldRow label="Target Geographic (Postal Code)">
          <Form.Item style={{ marginBottom: 0 }} name="PostalCode">
            <Select
              mode="multiple"
              placeholder="Select Postal Code"
              options={PostalCodeOptions}
              style={{ width: "100%" }}
              onChange={(value: any) => {
                onChangeRFM();
                setPostalFilter(value);
              }}
              showSearch={false}
              filterOption={false}
              maxTagCount={0}
              maxTagPlaceholder={() => {
                const selected: string[] =
                  form.getFieldValue("PostalCode") || [];
                if (!selected.length) return null;
                if (
                  selected.includes("all") ||
                  selected.length === postalTotal
                ) {
                  return "All Postal Sectors";
                }
                return `${selected.length}/${postalTotal} selected`;
              }}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Purchase Brand" required>
          <Form.Item
            style={{ marginBottom: 0 }}
            name="PurchaseBrand"
            rules={[{ required: true, message: "Please select Brand" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select Brands"
              maxTagCount={0}
              onChange={handleChangePurchaseBrand}
              value={
                form.getFieldValue("PurchaseBrand")?.includes(AllBrand)
                  ? [AllBrand, ...listPurchaseBrandFilter]
                  : form.getFieldValue("PurchaseBrand")
              }
              maxTagPlaceholder={() => {
                const value = form.getFieldValue("PurchaseBrand") || [];
                return value.includes(AllBrand)
                  ? AllBrand
                  : `${value.length}/${listPurchaseBrandFilter.length} selected`;
              }}
            >
              <Option key={AllBrand} value={AllBrand}>
                {AllBrand}
              </Option>
              {listPurchaseBrandFilter.map((brand) => (
                <Option key={brand} value={brand}>
                  {brand}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </FieldRow>
        <FieldRow label="Estimated Total Members">
          <Form.Item name="TotalMembers" style={{ marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <InputNumber
                style={{ width: isMobileOrTablet ? "70%" : "87%" }}
                value={estimatedTotalMember}
                addonAfter="Members"
                disabled
              />
              <Button
                disabled={!(hasCriteria && hasPeriod && hasSegment)}
                color="primary"
                variant="outlined"
                loading={loadingRefreshMember}
                onClick={handleRefreshMember}
                style={{
                  width: isMobileOrTablet ? "27%" : "12%",
                  marginLeft: "auto",
                }}
              >
                {loadingRefreshMember ? (
                  "Loading ..."
                ) : (
                  <>
                    <SyncOutlined /> Reload
                  </>
                )}
              </Button>
            </div>

            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              {changeRFM && (
                <Alert
                  type="error"
                  showIcon
                  description="The displayed total member count may not be accurate. Please click Reload to ensure the latest data is reflected based on your current selections."
                />
              )}

              {/* {!changeRFM && !alreadyReload && (
                <Alert
                  type="info"
                  showIcon
                  description="Estimation total number of members matched based on your selected RFM Period, Target Segment(s), RFM Criteria Profile, Target Demographics, and Target Geographic. Click Reload to view the result."
                />
              )} */}

              {firstReloadMember && !changeRFM ? (
                <div style={{ fontSize: 13, color: "#595959" }}>
                  Estimation total number of members matched based on your
                  selected RFM Period, Target Segment(s), RFM Criteria Profile,
                  Target Demographics, and Target Geographic.
                </div>
              ) : !changeRFM && alreadyReload && estimatedTotalMember > 0 ? (
                <Alert
                  type="info"
                  showIcon
                  icon={
                    <img
                      src="/icon/icon-memberlist.svg"
                      alt="members"
                      style={{ width: 40, height: 40 }}
                    />
                  }
                  message={
                    <Row align="middle" justify="space-between">
                      <Col>
                        <div style={{ fontSize: 20, fontWeight: 600 }}>
                          {estimatedTotalMember}
                        </div>
                        <div style={{ fontSize: 13, color: "#595959" }}>
                          Estimation total members will receive this campaign
                        </div>
                      </Col>

                      <Col>
                        <Button
                          type="primary"
                          icon={<EyeOutlined />}
                          onClick={openPreview}
                        >
                          Preview Members
                        </Button>
                      </Col>
                    </Row>
                  }
                />
              ) : (
                ""
              )}
            </div>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Channel Cost per Message">
          <Space.Compact style={{ width: "100%" }}>
            <Input
              disabled
              value="$"
              style={{ width: 40, textAlign: "center" }}
            />
            <Form.Item name="CostPerMember" noStyle>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                stringMode
                placeholder="0.00"
                disabled
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Cost to send one message, based on selected channel.
          </div>
        </FieldRow>

        <FieldRow label="Estimated Budget">
          <Form.Item name="EstimatedBudget" noStyle>
            <InputNumber
              style={{ width: "100%" }}
              value={
                estimatedTotalCost ? Number(estimatedTotalCost.toFixed(2)) : 0
              }
              disabled
            />
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Automatically calculated cost: Total Members X Channel Cost{" "}
              {selectedCampaignChannel === ChannelEnum.Sms && "X Total SMS"}
            </div>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Schedule Campaign" required>
          <Form.Item name="ScheduleType" noStyle rules={[{ required: true }]}>
            <Radio.Group onChange={onScheduleChange}>
              <Space direction="vertical">
                <Radio value={1}>
                  <Text>One‑Time Campaign</Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Send this campaign once at a specific date and time you
                    choose.
                  </div>
                </Radio>
                <Radio value="recurring" disabled>
                  <Text disabled>Recurring Campaign</Text>
                  <div
                    style={{
                      color: "rgba(0,0,0,0.25)",
                      fontWeight: "normal",
                    }}
                  >
                    Send this campaign on a repeated schedule—weekly or monthly.
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Target Members Mode" required>
          <Form.Item name="Mode" noStyle rules={[{ required: true }]}>
            <Radio.Group onChange={onModeChange} style={{ width: "100%" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio value={ModeEnum.Refresh}>
                  <Text>Refresh target before sending</Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Target members will be refreshed 1 day before send time.
                  </div>
                </Radio>
                <Alert
                  description={
                    <>
                      Target members may change before sending. This can affect
                      final reach and budget usage.
                    </>
                  }
                  type="warning"
                  showIcon
                />
                <Radio value={ModeEnum.Fixed}>
                  <Text>Use fixed target</Text>
                  <div
                    style={{
                      color: "grey",
                      fontWeight: "normal",
                    }}
                  >
                    Target members remains the same as when the campaign was
                    created.{" "}
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FieldRow>
        {scheduleType === TypeEnum.Onetime ? (
          <FieldRow label="Send Date & Time" required>
            <Form.Item
              name="CampaignDate"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Select date and time" }]}
            >
              <DatePicker
                showTime={{
                  format: "HH:mm",
                  minuteStep: 15,
                  showSecond: false,
                }}
                disabledDate={(current) => {
                  if (!current) return false;

                  return (
                    current.isBefore(startOfToday, "day") ||
                    current.isAfter(endOfNextMonth, "day")
                  );
                }}
                disabledTime={(date) => {
                  if (!date) return {};

                  const base = buildDisabledTime(date, blocked);

                  return applyPastTimeToday(date, base);
                }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%", height: "unset !important" }}
                placeholder="Select Send Date & Time"
              />
            </Form.Item>
            {campaignDate && (
              <Alert
                description={
                  <>
                    {
                      <>
                        Your campaign will start at {formattedStart} and is
                        estimated to complete by {formattedEnd}.
                      </>
                    }
                  </>
                }
                type="info"
                showIcon
                style={{ marginTop: 10 }}
              />
            )}
          </FieldRow>
        ) : (
          <>
            <FieldRow label="Recurring Schedule" required>
              <Space wrap>
                <Form.Item
                  name="recurringRule"
                  rules={[{ required: true, message: "Select frequency" }]}
                  noStyle
                >
                  <Select
                    placeholder="Frequency"
                    style={{ width: 180 }}
                    options={[
                      { value: "Daily", label: "Daily" },
                      { value: "Weekly", label: "Weekly" },
                      { value: "Monthly", label: "Monthly" },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="recurringTime"
                  rules={[{ required: true, message: "Select time" }]}
                  noStyle
                >
                  <DatePicker showTime format="HH:mm" picker="time" />
                </Form.Item>
              </Space>
            </FieldRow>
          </>
        )}

        <FieldRow label="Budget Amount Entire Campaign" required>
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item
              name="Budget"
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  message: "Please input total campaign budget",
                },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    return Number(value) >= cost
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`Minimum required budget: ${cost}`),
                        );
                  },
                },
              ]}
              style={{ width: "100%", marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                step={0.01}
                addonBefore="$"
                style={{ width: "100%" }}
                placeholder="Input Total Campaign Budget"
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Applies to this{" "}
            {scheduleType === TypeEnum.Onetime ? "one‑time" : "recurring"}{" "}
            campaign.
          </div>
        </FieldRow>

        <FieldRow label="Maximum Reach">
          <InputNumber
            style={{ width: "100%" }}
            value={maxReach}
            addonAfter="Members"
            formatter={(value) => {
              if (!value) return `0 / ${estimatedTotalMember}`;

              return `${Number(value)} / ${estimatedTotalMember}`;
            }}
            disabled
          />
          {maxReach > estimatedTotalMember ? (
            <Alert
              className="alert-info-budget-higher"
              type="info"
              showIcon
              style={{ padding: "8px 12px", marginTop: 10 }}
              description={
                <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Your budget is higher than required. Some budget may not be
                    used.
                  </div>
                  <div>
                    Adjust your budget or expand your targeting to utilize it
                    fully.
                  </div>
                </div>
              }
            />
          ) : maxReach < estimatedTotalMember ? (
            <Alert
              style={{ marginTop: 10 }}
              description={
                <>
                  Only part of your target members will be reached based on your
                  budget.
                </>
              }
              type="warning"
              showIcon
            />
          ) : (
            <div style={{ color: "#8c8c8c", marginTop: 10 }}>
              Recipients are selected from the top of the preview member list,
              up to the maximum number covered by your current budget.
            </div>
          )}
        </FieldRow>

        {/* <Row>
          <Col span={24}>
            <Alert
              message={<strong> Important Notice</strong>}
              description={
                <>
                  Estimation Total Members and Estimated Budget displayed are
                  estimates based on currently selected criteria. Actual member
                  count and budget used when the campaign runs may differ due to
                  real-time data changes, system conditions, or other factors.
                </>
              }
              type="warning"
              showIcon
            />
          </Col>
        </Row> */}

        {/* Message Content */}
        <Title level={5} style={{ marginBottom: 12 }}>
          Message Content
        </Title>

        <Divider />
        {SHOW_REWARD_TYPES.includes(selectedType) && (
          <FieldRow label="Rewards to Include" required>
            <Form.Item
              name="Voucher"
              style={{ width: "100%", marginBottom: 0 }}
              rules={[
                {
                  validator: (_, value) => {
                    if (selectedVoucher || selectedEVoucher) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Please choose rewards to include"),
                    );
                  },
                },
              ]}
            >
              {selectedVoucher || selectedEVoucher ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={summary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Rewards Selected:
                      </span>
                    }
                    prefix={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                    suffix={
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteFilled />}
                        onClick={() => {
                          setSelectedKey(null);
                          setSelectedVoucher(null);
                          setSelectedEVoucher(null);
                        }}
                      />
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedRow ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setVoucherOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setVoucherOpen(true)}
                      type="primary"
                    >
                      Select Rewards
                    </Button>
                  </Col>
                  <Col lg={20} xs={24}>
                    <Text type="secondary">No rewards selected</Text>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </FieldRow>
        )}
        {(selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
          selectedCampaignChannel === ChannelEnum.WhatsAppPremium) && (
          <FieldRow label="Campaign Image (Optional)">
            <Form.Item name="Image" style={{ width: "100%", marginBottom: 0 }}>
              <Row gutter={[12, 12]}>
                <Col lg={4} xs={24}>
                  <Upload
                    accept="image/png,image/jpeg"
                    listType="picture"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    maxCount={1}
                    className="w-100"
                    onChange={handleChangeUpload}
                  >
                    <Button
                      className="w-100"
                      disabled={previewUrl ? true : false}
                    >
                      Choose File
                    </Button>
                  </Upload>
                </Col>
                <Col lg={20} xs={24}>
                  {" "}
                  {fileList.length === 0 ? (
                    <Text type="secondary">
                      No file chosen <br />
                      Recommended Size: 1080×1080 (1:1) or 1920×1080 (16:9),
                      JPG/PNG, Max 5 MB.
                    </Text>
                  ) : isUploading ? (
                    <Progress
                      percent={progressUpload}
                      size="small"
                      status="active"
                    />
                  ) : (
                    <Space>
                      <Text>{fileList[0].name} </Text>
                      <Button
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                        onClick={handleRemove}
                      >
                        Remove
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: 15, marginBottom: 15 }}>
                {previewUrl && (
                  <AntdImage
                    src={previewUrl}
                    alt="Campaign preview"
                    width={250}
                    height={150}
                    style={{
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #f0f0f0",
                    }}
                    preview={{ src: previewUrl }}
                  />
                )}
              </Row>
            </Form.Item>
          </FieldRow>
        )}
        {/* <FieldRow label="Message Template">
          <Form.Item name="messageTemplate" noStyle>
            <Select
              placeholder="Select Message Template"
              options={MessageTemplates}
              allowClear
              style={{ width: "100%" }}
            />
          </Form.Item>
        </FieldRow> */}

        <FieldRow label="Message Content" required>
          <Form.Item
            name="Message"
            style={{ marginBottom: 0 }}
            rules={[
              {
                required: true,
                message: "Enter Message Content",
                validator: (_, value) => {
                  const text = value?.replace(/<[^>]*>/g, "").trim();
                  return text
                    ? Promise.resolve()
                    : Promise.reject("Enter Message Content");
                },
              },
            ]}
          >
            {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
              // <Editor
              //   value={msg}
              //   onChange={(val) => {
              //     form.setFieldsValue({ Message: val });
              //     form.validateFields(["Message"]);
              //   }}
              // />
              <EditorQuill
                value={msg}
                onChange={(val) => {
                  // if (isFirstChange.current) {
                  //   return;
                  // }
                  if (val === "<p><br></p>")
                    form.setFieldsValue({ Message: val });
                  // form.validateFields(["Message"]);
                }}
              />
            )}
            {selectedCampaignChannel === ChannelEnum.Sms && (
              <Input.TextArea
                rows={4}
                placeholder="Enter Message Content"
                autoSize
              />
            )}
            {selectedCampaignChannel === ChannelEnum.WhatsAppPremium &&
              (selectedMessageTemplates ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={templatesSummary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Template Selected:
                      </span>
                    }
                    prefix={
                      selectedMessageTemplates ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    suffix={
                      selectedMessageTemplates ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => {
                            setSelectedMessageTemplates(null);
                          }}
                        />
                      ) : null
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedMessageTemplates ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setMessageTemplatesOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setMessageTemplatesOpen(true)}
                      type="primary"
                    >
                      Select Template
                    </Button>
                  </Col>
                  <Col
                    lg={20}
                    xs={24}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary">No template selected</Text>
                  </Col>
                </Row>
              ))}
          </Form.Item>
        </FieldRow>
        {selectedCampaignChannel !== ChannelEnum.WhatsAppPremium && (
          <FieldRow
            label={
              <span>
                Message Variables{" "}
                <Tooltip
                  title={
                    <>
                      Personalize your message with variables.
                      <br />
                      These will be replaced with real data when the campaign is
                      sent
                    </>
                  }
                >
                  <InfoCircleOutlined
                    style={{ color: "#8c8c8c", cursor: "pointer" }}
                  />
                </Tooltip>
              </span>
            }
          >
            {" "}
            <div style={{ marginTop: 8 }}>
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {filteredVars.map((v) => (
                    <Tag
                      key={v}
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVar(v)}
                    >
                      {v}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          </FieldRow>
        )}
        {/* <FieldRow
          label={
            <span>
              Default Message{" "}
              <Tooltip
                title={
                  <>
                    Personalize your message with variables.
                    <br />
                    These will be replaced with real data when the campaign is
                    sent
                  </>
                }
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", cursor: "pointer" }}
                />
              </Tooltip>
            </span>
          }
        >
          <Switch
            checked={enableTrackingLink}
            onChange={setEnableTrackingLink}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </FieldRow> */}
        {link && selectedCampaignChannel !== ChannelEnum.WhatsAppStandard && (
          <FieldRow label={<span>Campaign Tracking Link (CTA)</span>}>
            <Form.Item
              name="Link"
              label=""
              rules={[
                { required: false },
                { type: "url", message: "Please enter a valid URL" },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="Enter a Call to Action Link"
                value={link}
                disabled={true}
              />
              <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                This link is automatically generated for your campaign tracking.
                Allowing you to monitor customer engagement and clicks.{" "}
              </div>
            </Form.Item>
          </FieldRow>
        )}
        <FieldRow
          label={
            <span>
              Preview Message{" "}
              <Tooltip
                title={
                  <>
                    This preview message shows the content message
                    <br /> along with the Campaign Tracking Link (CTA),
                    <br /> While the Unsubscribe link displayed is only a
                    placeholder.
                  </>
                }
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", cursor: "pointer" }}
                />
              </Tooltip>
            </span>
          }
        >
          {/* Preview */}
          <div style={{ marginTop: 16 }}>
            <Text strong>Live Preview</Text>
            <Card size="small" style={{ marginTop: 8 }}>
              <div style={{ color: "#8c8c8c" }}>
                {previewUrl && (
                  <>
                    <AntdImage
                      src={previewUrl}
                      alt="Campaign preview"
                      width={250}
                      height={150}
                      style={{
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                      }}
                      preview={false}
                    />
                    <br />
                  </>
                )}
                <div
                  className="preview-update-campaign"
                  dangerouslySetInnerHTML={{
                    __html: previewHtml,
                  }}
                />
                {selectedCampaignChannel === ChannelEnum.WhatsAppPremium && (
                  <Space
                    direction="vertical"
                    style={{ width: "100%", marginTop: 16 }}
                  >
                    {selectedMessageTemplates?.TextLink1 && (
                      <Button
                        block
                        style={{
                          color: "#00A884",
                          fontWeight: 600,
                        }}
                        icon={
                          <img
                            src="/icon/icon-button-whatsapp.svg"
                            style={{ width: 16 }}
                          />
                        }
                      >
                        {selectedMessageTemplates?.TextLink1}
                      </Button>
                    )}

                    {selectedMessageTemplates?.TextLink2 && (
                      <Button
                        block
                        style={{
                          color: "#00A884",
                          fontWeight: 600,
                        }}
                        icon={
                          <img
                            src="/icon/icon-button-whatsapp.svg"
                            style={{ width: 16 }}
                          />
                        }
                      >
                        {selectedMessageTemplates?.TextLink2}
                      </Button>
                    )}
                  </Space>
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
                  <>
                    <br />
                    👉 Reply with{" "}
                    <b>OK to receive your membership login link</b>
                    <br />
                    👉 Reply with{" "}
                    <b>STOP to opt out of future WhatsApp messages</b>
                  </>
                )}{" "}
              </div>
            </Card>
            <Row justify="space-between" style={{ marginTop: 6 }}>
              <Col>
                <Text type="secondary">Characters used: {charCount}</Text>
              </Col>
              <Col>
                {selectedCampaignChannel === ChannelEnum.Sms && (
                  <Text type="secondary">Total SMS: {smsSegments}</Text>
                )}
              </Col>
            </Row>
          </div>
        </FieldRow>
        {selectedCampaignChannel === ChannelEnum.Sms && (
          <Alert
            message={<strong> Important Notice</strong>}
            description={
              <>
                Some messages may use extra SMS when variables are applied. This
                can affect total recipients covered by your budget.
              </>
            }
            type="warning"
            showIcon
          />
        )}

        {/* Bottom actions */}
        {!isMobileOrTablet ? (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col></Col>
            <Col>
              <Button
                loading={loading || loadingRefreshMember}
                type="primary"
                size="large"
                onClick={() => {
                  form.submit();
                }}
              >
                Submit
              </Button>
            </Col>
          </Row>
        ) : (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col xs={12}></Col>
            <Col xs={12}>
              <Button
                className="w-100"
                type="primary"
                loading={loading || loadingRefreshMember}
                onClick={() => {
                  form.submit();
                }}
              >
                Submit
              </Button>
            </Col>
          </Row>
        )}
      </Card>
      <Modal
        title="Select Voucher"
        open={voucherOpen}
        onCancel={() => {
          setSelectedKey(null);
          setSelectedRow(null);
          setVoucherOpen(false);
        }}
        footer={null}
        maskClosable={false}
        width={1200}
      >
        <Row justify={"space-between"}>
          <Col span={12}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={`Search by ${
                selectedType === CampaignTypeEnum.EVoucher ? "E-" : ""
              }Voucher's name`}
              value={searchVoucher}
              onChange={(e) => setSearchVoucher(e.target.value)}
            />
          </Col>
          <Col span={12} style={{ textAlign: "end" }}>
            <a
              href={`${
                selectedType === CampaignTypeEnum.EVoucher
                  ? process.env.REACT_APP_DASHBOARD_GETZPASS_URL
                  : process.env.REACT_APP_DASHBOARD_URL
              }${
                selectedType === CampaignTypeEnum.EVoucher
                  ? "/Product#/e-voucher/create/0"
                  : "/voucher/create"
              }`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button type="primary">
                {" "}
                <PlusOutlined />{" "}
                {selectedType === CampaignTypeEnum.EVoucher
                  ? "Create E-Voucher"
                  : "Create Voucher"}
              </Button>
            </a>
          </Col>
        </Row>

        <Space direction="vertical" style={{ width: "100%" }}>
          <VoucherTable
            selectedType={selectedType}
            voucherData={filteredVouchers}
            eVoucherData={filteredVEouchers}
            selectedKey={selectedKey}
            onChangeSelected={(key, row) => {
              setSelectedKey(key);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
              if (selectedType === CampaignTypeEnum.EVoucher) {
                setSelectedEVoucher(row);
              }
            }}
            onAddSelect={(row) => {
              setSelectedKey(row.Id);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
              if (selectedType === CampaignTypeEnum.EVoucher) {
                setSelectedEVoucher(row);
              }
              setVoucherOpen(false);
            }}
            loading={loadingVoucher}
          />
        </Space>
      </Modal>
      <BillingModal
        open={isBillingModalOpen}
        campaignName={campaignName}
        channel={selectedCampaignChannel}
        estimatedMembers={estimatedTotalMember}
        costPerMessage={cost}
        smsSegments={smsSegments}
        maxBudget={pendingPayload?.Budget}
        mode={pendingPayload?.Mode}
        campaignStart={pendingPayload?.CampaignStart}
        campaignEnd={pendingPayload?.CampaignEnd}
        confirmLabel="Submit Campaign"
        onClose={() => setIsBillingModalOpen(false)}
        onPaymentLater={onPaymentLater}
        onConfirmPayment={async (_email, referenceCode) => {
          try {
            setLoading(true);
            const resultId = await campaignService.updateCampaignDetails({
              ...pendingPayload!,
              IsBilled: 1,
              ReferenceCode: referenceCode,
            });
            if (!resultId) throw new Error("Failed to create campaign");
          } catch {
            console.error(
              "Error try catch when create campaign with confirm payment",
            );
          } finally {
            setLoading(false);
          }
        }}
        onSuccess={() => {
          setIsBillingModalOpen(false);
          setIsPaymentSuccessModalOpen(true);
        }}
        onFailed={() => {
          setIsBillingModalOpen(false);
          setIsPaymentFailedModalOpen(true);
        }}
      />
      <Modal
        open={isPaymentSuccessModalOpen}
        onCancel={() => setIsPaymentSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign Updated Successfully
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign <b>{campaignName}</b> has been updated and payment has
            been processed successfully.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              setIsPaymentSuccessModalOpen(false);
              navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
            }}
            style={{ marginTop: 8 }}
          >
            View Details
          </Button>
        </Row>
      </Modal>
      <Modal
        open={isPaymentFailedModalOpen}
        onCancel={() => setIsPaymentFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}
        >
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#ff4d4f" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Payment Failed
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign <b>{campaignName}</b> was updated but payment could
            not be processed. You can retry payment from the campaign detail
            page.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Button
              size="large"
              className="w-100"
              onClick={() => {
                setIsPaymentFailedModalOpen(false);
                onPaymentLater();
              }}
              style={{ marginTop: 8 }}
            >
              Set Up Payment Later
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              size="large"
              className="w-100"
              onClick={() => {
                setIsPaymentFailedModalOpen(false);
                setIsBillingModalOpen(true);
              }}
              style={{ marginTop: 8 }}
            >
              Retry Payment
            </Button>
          </Col>
        </Row>
      </Modal>
      <Modal
        open={isModalWarningCreateCampaignVisible}
        onCancel={() => setIsModalWarningCreateCampaignVisible(false)}
        footer={null}
        centered
        width={620}
        closeIcon={<CloseOutlined />}
        styles={{ body: { paddingTop: 28, paddingBottom: 16 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}
        >
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#EEB711" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Send with Partial Reach
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your budget will only cover <b>{maxReach}</b> of{" "}
            <b>{estimatedTotalMember}</b> targeted members. Do you want to
            continue?
          </Text>

          <Space style={{ marginTop: 12 }}>
            <Button
              size="large"
              onClick={() => setIsModalWarningCreateCampaignVisible(false)}
            >
              Go Back
            </Button>
            <Button
              type="primary"
              size="large"
              // onClick={() => handleSaveCampaign()}
            >
              Send Anyway
            </Button>
          </Space>
        </Space>
      </Modal>
      <RFMGeneratingModal
        visible={loading || loadingRefreshMember}
        message="Preparing Campaign Data..."
      />
      <CustomModal
        type="timeout"
        title="Editing Unavailable"
        message={
          isBilled
            ? "This campaign can no longer be edited after payment is completed."
            : "The campaign is approaching its scheduled send time, so editing is no longer available."
        }
        isVisible={showTimeout}
        onClose={() => setShowTimeout(false)}
        autoClose={false}
      />
      <MessageTemplatesModal
        campaignType={selectedType}
        open={messageTemplatesOpen}
        templates={listMessageTemplates}
        onCancel={() => setMessageTemplatesOpen(false)}
        onSelect={(template) => {
          setSelectedMessageTemplates(template);
          setMessageTemplatesOpen(false);
        }}
        selectedTemplates={selectedMessageTemplates ?? undefined}
      />
      <PreviewMemberModal
        open={isPreviewOpen}
        onClose={closePreview}
        estimatedMember={estimatedMember}
      />
    </Form>
  );
};

export default UpdateCampaign;
